// src/store/modules/login/index.js

import { isEmbedMode } from '@/utils/embedMode';

function loadFromStorage() {
  const userInfoStr = sessionStorage.getItem('userInfo');
  let userInfo = null;
  try {
    userInfo = userInfoStr ? JSON.parse(userInfoStr) : null;
  } catch (e) {
    console.warn('Failed to parse userInfo from sessionStorage:', e);
  }
  
  const supportRoomStr = sessionStorage.getItem('support_room');
  let support_room = null;
  try {
    support_room = supportRoomStr ? JSON.parse(supportRoomStr) : null;
  } catch (e) {
    console.warn('Failed to parse support_room from sessionStorage:', e);
  }
  
  const internalRoomStr = sessionStorage.getItem('internal_room');
  let internal_room = null;
  try {
    internal_room = internalRoomStr ? JSON.parse(internalRoomStr) : null;
  } catch (e) {
    console.warn('Failed to parse internal_room from sessionStorage:', e);
  }
  
  return {
    token: sessionStorage.getItem('token') || '',
    userId: sessionStorage.getItem('userId') || '',
    exp: parseInt(sessionStorage.getItem('exp') || '0'),
    refreshToken: sessionStorage.getItem('refreshToken') || '',
    refreshTokenExpiry: parseInt(sessionStorage.getItem('refreshTokenExpiry') || '0'),
    authToken: sessionStorage.getItem('authToken') || '',
    rc_user_id: sessionStorage.getItem('rc_user_id') || '',
    ws_url: sessionStorage.getItem('ws_url') || '',
    support_room: support_room,
    internal_room: internal_room,
    userInfo: userInfo
  };
}

function saveToStorage(state) {
  sessionStorage.setItem('token', state.token || '');
  sessionStorage.setItem('userId', state.userId || '');
  sessionStorage.setItem('exp', (state.exp || 0).toString());
  sessionStorage.setItem('refreshToken', state.refreshToken || '');
  sessionStorage.setItem('refreshTokenExpiry', (state.refreshTokenExpiry || 0).toString());
  sessionStorage.setItem('authToken', state.authToken || '');
  sessionStorage.setItem('rc_user_id', state.rc_user_id || '');
  sessionStorage.setItem('ws_url', state.ws_url || '');
  sessionStorage.setItem('support_room', state.support_room ? JSON.stringify(state.support_room) : '');
  sessionStorage.setItem('internal_room', state.internal_room ? JSON.stringify(state.internal_room) : '');
  sessionStorage.setItem('userInfo', state.userInfo ? JSON.stringify(state.userInfo) : '');
}

function clearStorage() {
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('userId');
  sessionStorage.removeItem('exp');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('refreshTokenExpiry');
  sessionStorage.removeItem('authToken');
  sessionStorage.removeItem('rc_user_id');
  sessionStorage.removeItem('ws_url');
  sessionStorage.removeItem('support_room');
  sessionStorage.removeItem('internal_room');
  sessionStorage.removeItem('userInfo');
  
  // Also clear localStorage to prevent stale tokens
  localStorage.removeItem('jwtToken');
}

const persisted = loadFromStorage();

export default {
  namespaced: true,
  
  state: {
    token: persisted?.token || '',
    userId: persisted?.userId || '',
    exp: persisted?.exp || 0, // epoch seconds (optional)
    refreshToken: persisted?.refreshToken || '', // long-lived refresh token
    refreshTokenExpiry: persisted?.refreshTokenExpiry || 0, // epoch seconds
    authToken: persisted?.authToken || '', // RocketChat auth token
    rc_user_id: persisted?.rc_user_id || '', // RocketChat user ID
    ws_url: persisted?.ws_url || '', // WebSocket URL
    support_room: persisted?.support_room || null, // Support room info
    internal_room: persisted?.internal_room || null, // Internal room info
    userInfo: persisted?.userInfo || null, // User info from getUserInfoV2
  },
  
  getters: {
    isAuthed: (state) =>
      !!state.token && (!state.exp || state.exp * 1000 > Date.now()),
    
    token: (state) => state.token,
    userId: (state) => state.userId,
    exp: (state) => state.exp,
    refreshToken: (state) => state.refreshToken,
    refreshTokenExpiry: (state) => state.refreshTokenExpiry,
    authToken: (state) => state.authToken,
    rcUserId: (state) => state.rc_user_id,
    wsUrl: (state) => state.ws_url,
    userInfo: (state) => state.userInfo,
    
    // User role getters for clinic dashboard
    isAdmin: (state) => state.userInfo?.user_roles?.includes('clinic') || false,
    customerId: (state) => state.userInfo?.customer_id?.toString() || '',
    clinicId: (state) => state.userInfo?.clinic_id?.toString() || '',
    
    // For axios interceptor
    authHeaders: (state) => {
      if (state.authToken && state.rcUserId) {
        return {
          'X-Auth-Token': state.authToken,
          'X-User-Id': state.rcUserId
        };
      } else if (state.token) {
        return {
          'X-Auth-Token': state.token,
          'X-User-Id': state.userId
        };
      }
      return {};
    }
  },
  
  mutations: {
    SET_TOKEN(state, { token, userId, exp, refreshToken, refreshTokenExpiry, authToken, rc_user_id, ws_url, support_room, internal_room, userInfo }) {
      state.token = token;
      state.userId = userId || '';
      state.exp = exp || 0;
      if (refreshToken !== undefined) {
        state.refreshToken = refreshToken;
      }
      if (refreshTokenExpiry !== undefined) {
        state.refreshTokenExpiry = refreshTokenExpiry;
      }
      // Guard RC creds like support_room/internal_room: only overwrite when the
      // field is provided. This stops a bare setToken({ token, userId, exp }) on
      // reload (or after an rc-session failure) from wiping persisted RC creds.
      // Callers that genuinely clear them (logout, clinic switch, new session)
      // pass explicit '' values, which still take effect.
      if (authToken !== undefined) {
        state.authToken = authToken;
      }
      if (rc_user_id !== undefined) {
        state.rc_user_id = rc_user_id;
      }
      if (ws_url !== undefined) {
        state.ws_url = ws_url;
      }
      if (support_room !== undefined) {
        state.support_room = support_room;
      }
      if (internal_room !== undefined) {
        state.internal_room = internal_room;
      }
      if (userInfo !== undefined) {
        state.userInfo = userInfo;
      }
      saveToStorage(state);
    },
    
    // Partial update for the silent token-refresh flow. Only touches the access
    // token / refresh token fields so RocketChat creds and userInfo are preserved.
    UPDATE_TOKENS(state, { token, exp, refreshToken, refreshTokenExpiry }) {
      const previousToken = state.token;
      if (token !== undefined) {
        state.token = token;
      }
      if (exp !== undefined) {
        state.exp = exp || 0;
      }
      if (refreshToken !== undefined) {
        state.refreshToken = refreshToken;
      }
      if (refreshTokenExpiry !== undefined) {
        state.refreshTokenExpiry = refreshTokenExpiry || 0;
      }
      saveToStorage(state);

      // Keep the legacy order-test "act-as" token (`jwtToken` in session/local
      // storage) in sync with the rotated access token — but ONLY when it still
      // mirrors the token we just rotated out (i.e. the user is acting as self).
      // When a multiLogin / provider token is active, jwtToken differs from the
      // portal token, so this leaves it untouched and never clobbers that context.
      if (token !== undefined && token && token !== previousToken && previousToken) {
        if (sessionStorage.getItem('jwtToken') === previousToken) {
          sessionStorage.setItem('jwtToken', token);
        }
        if (localStorage.getItem('jwtToken') === previousToken) {
          localStorage.setItem('jwtToken', token);
        }
      }
    },

    CLEAR(state) {
      state.token = '';
      state.userId = '';
      state.exp = 0;
      state.refreshToken = '';
      state.refreshTokenExpiry = 0;
      state.authToken = '';
      state.rc_user_id = '';
      state.ws_url = '';
      state.support_room = null;
      state.internal_room = null;
      state.userInfo = null;
      clearStorage();
    },
    
    SET_USER_INFO(state, userInfo) {
      state.userInfo = userInfo;
      saveToStorage(state);
    },
  },
  
  actions: {
    setToken({ commit }, payload) { 
      commit('SET_TOKEN', payload); 
    },
    
    updateTokens({ commit }, payload) {
      commit('UPDATE_TOKENS', payload);
    },
    
    setUserInfo({ commit }, userInfo) {
      commit('SET_USER_INFO', userInfo);
    },
    
    clear({ commit }) { 
      commit('CLEAR'); 
    },
    
    // Manual logout (sign out button)
    logout({ commit, dispatch }) {
      console.log('[login] Logout: clearing auth state');
      dispatch('clear');
      
      // Don't redirect in development mode
      if (process.env.NODE_ENV === 'development') {
        console.log('Development mode: skipping redirect to login page');
        return;
      }

      // In embed mode (iframe inside va-portal) we must NOT self-navigate:
      // setting window.location.href would load the portal login page INSIDE the
      // frame. va-portal owns auth and will handle re-login / reload the iframe,
      // so just clear state and leave navigation to the parent. Mirrors
      // sessionManager.redirectToLogin()'s embed guard — handleAuthError (401
      // interceptors) routes here, bypassing that guard otherwise.
      if (isEmbedMode()) {
        console.log('Embed mode: skipping self-redirect to login; parent portal owns auth');
        return;
      }

      // Redirect to login page based on environment
      const loginUrl = process.env.VUE_APP_ENV === 'production'
        ? 'https://portal.vibrant-wellness.com/#/login'
        : 'https://staging.va-portal.pages.dev/#/login';
      
      window.location.href = loginUrl;
    },
    
    // Handle token expiry/401 errors
    handleAuthError({ commit, dispatch }) {
      console.warn('Authentication error detected, clearing auth state');
      dispatch('logout');
    }
  },
};